import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";
import {
  Megaphone,
  Users,
  FileText,
  Plus,
  Mail,
  MessageSquare,
  Copy,
  Trash2,
  AlertTriangle,
  Send,
  Filter,
  UserPlus,
  Search,
  X,
  ShieldCheck,
  Pencil,
  ClipboardList,
  Home,
  Award,
  RefreshCw,
  Database,
  BarChart3,
  Phone,
  Ban,
  Eye,
  Bot,
} from "lucide-react";
import { suppressEmail } from "../../../api/notifications";
import {
  fetchCampaigns,
  fetchCampaign,
  fetchCampaignRecipients,
  fetchSendBatch,
  fetchSendBatchRecipients,
  deleteCampaign,
  duplicateCampaign,
  fetchSegments,
  fetchSegment,
  fetchSegmentMembers,
  createSegment,
  updateSegment,
  deleteSegment,
  previewSegment,
  searchContactsForList,
  parsePastedEmails,
  fetchFilterOptions,
  fetchTemplates,
  fetchMergeTags,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  fetchCommsAnalytics,
  fetchSmsCreditBalance,
} from "../api/communicationsApi";
import AgencyScopeSwitch, {
  useAgencyScope,
  useExtraAgents,
  IncludeAgentsPicker,
} from "../../../components/common/AgencyScopeSwitch";
import { getAgents } from "../../../api/agent";
import EmailBuilder from "../components/EmailBuilder";
import SentActivityTab from "../components/SentActivityTab";
import UnsubscribedTab from "../components/UnsubscribedTab";
import { smsInfo, asSent, SMS_STOP_PLACEHOLDER, appendStopPlaceholder } from "../utils/smsCredits";
import MessengerPage from "../../messenger/pages/MessengerPage";
import GeeveeConversationsTab from "../../geevee-sms/GeeveeConversationsTab";
import AutoEmailResponses from "../../../pages/AutoEmailResponses";
import ContactLink from "../../contacts/components/ContactLink";
import BuyerActionsModal from "../../../components/properties/BuyerActionsModal";

const TABS = [
  { id: "campaigns", label: "Campaigns", icon: Megaphone },
  // "how many texts/emails have gone out and who has received the texts"
  // (CG, Monday 12887883570, 1 Sep 2026).
  { id: "sent", label: "Sent", icon: BarChart3 },
  // "Please confirm this is being tracked as I cant see who has unsubscribed"
  // (Paul Jimoulis, CRM-2026-00093, 6 Oct 2026).
  { id: "unsubscribed", label: "Unsubscribed", icon: Ban },
  { id: "lists", label: "Contact Lists", icon: Users },
  { id: "appraisals", label: "Appraisals", icon: Award },
  { id: "past_clients", label: "Past Clients", icon: Home },
  { id: "templates", label: "Templates", icon: FileText },
  { id: "messenger", label: "Messenger", icon: MessageSquare },
  // GeeVee texting buyers from the Collings number, replies and all
  // (Christian, Monday 13432357091).
  { id: "geevee_sms", label: "GeeVee SMS", icon: Bot },
  { id: "auto_responses", label: "Auto-Responses", icon: RefreshCw },
];

const STATUS_STYLES = {
  draft: "bg-gray-100 text-gray-600",
  scheduled: "bg-amber-100 text-amber-700",
  sending: "bg-blue-100 text-blue-700",
  sent: "bg-green-100 text-green-700",
  paused: "bg-orange-100 text-orange-700",
  cancelled: "bg-gray-100 text-gray-500",
  failed: "bg-red-100 text-red-700",
};

function ChannelIcon({ channel }) {
  return channel === "sms" ? (
    <MessageSquare className="w-4 h-4 text-purple-500" />
  ) : (
    <Mail className="w-4 h-4 text-blue-500" />
  );
}

/* ------------------------------- Campaigns ------------------------------- */
// Drill-down listing the recipients behind a campaign stat (sent / opened /
// clicked), so agents can see exactly who was sent to, who opened and who
// clicked (CRM-2026-00041).
function CampaignRecipientsModal({ campaign, mode, onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  // Which rows have already been rung. Local to this panel and this sitting:
  // it is a place-keeper for working down the call list, not a logged activity.
  const [called, setCalled] = useState(() => new Set());
  // The recipient whose quick-actions popup is open (log a call note, mark them
  // Purchased, set a reminder), so an agent can act on the outcome of the call
  // without leaving the list (CRM-2026-00080).
  const [actionsFor, setActionsFor] = useState(null);

  // A blast (a Find Buyers send) has no CampaignRecipient rows - its recipients
  // live on the communication log - so it is read from its own endpoint. The API
  // client maps both onto one shape, so everything below is identical.
  const isBlast = campaign.kind === "blast";

  useEffect(() => {
    let active = true;
    setLoading(true);
    const fetcher = isBlast ? fetchSendBatchRecipients : fetchCampaignRecipients;
    fetcher(campaign.id, { engaged: mode })
      .then((d) => active && setRows(Array.isArray(d) ? d : []))
      .catch(() => active && toast.error("Couldn't load recipients"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [campaign.id, mode, isBlast]);

  const heading = { sent: "Recipients", opened: "Opened", clicked: "Clicked", unsubscribed: "Unsubscribed" }[mode];
  const tsField = { sent: "sent_at", opened: "opened_at", clicked: "clicked_at", unsubscribed: "unsubscribed_at" }[mode];
  const tsLabel = { sent: "Sent", opened: "Opened", clicked: "Clicked", unsubscribed: "Unsubscribed" }[mode];
  const emptyMsg = {
    sent: "No recipients yet.",
    opened: "No one has opened this campaign yet.",
    clicked: "No one has clicked a link in this campaign yet.",
    unsubscribed: "No one has unsubscribed from this send.",
  }[mode];

  const toggleCalled = (id) =>
    setCalled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const phoneOf = (r) => (r.contact_phone || "").trim();
  const withPhone = rows.filter(phoneOf);
  // People who unsubscribed asked to be left alone, so their list is for
  // knowing who, not a call list: no dialler copy, no tap-to-call, no actions.
  const callable = mode !== "unsubscribed";

  // Copying the whole column suits agents who would rather paste the list into
  // a dialler or their phone than click each row in turn.
  const copyNumbers = async () => {
    try {
      await navigator.clipboard.writeText(withPhone.map(phoneOf).join("\n"));
      toast.success(`Copied ${withPhone.length} number(s)`);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[820px] max-w-full max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">{heading} — {campaign.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading
                ? "Loading…"
                : callable
                  ? `${rows.length} recipient(s) · ${withPhone.length} with a number` +
                    (called.size ? ` · ${called.size} called` : "")
                  : `${rows.length} unsubscribed · already blocked from future sends`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!loading && callable && withPhone.length > 0 && (
              <button
                onClick={copyNumbers}
                title="Copy every number on this list"
                className="text-xs font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" /> Copy numbers
              </button>
            )}
            <button onClick={onClose} className="text-gray-500"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">{emptyMsg}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                  {callable && <th className="py-1.5 w-6"></th>}
                  <th className="py-1.5">Name</th>
                  <th className="py-1.5">Mobile</th>
                  <th className="py-1.5">Email</th>
                  <th className="py-1.5">{tsLabel}</th>
                  {callable && <th className="py-1.5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const phone = phoneOf(r);
                  const done = called.has(r.id);
                  return (
                    <tr key={r.id} className={`border-t border-gray-100 ${done ? "bg-gray-50" : ""}`}>
                      {callable && (
                      <td className="py-1.5 pr-1">
                        <input
                          type="checkbox"
                          checked={done}
                          onChange={() => toggleCalled(r.id)}
                          title={done ? "Called" : "Mark as called"}
                          className="w-3.5 h-3.5 accent-[#55C5D0] cursor-pointer"
                        />
                      </td>
                      )}
                      <td className={`py-1.5 pr-2 font-semibold ${done ? "text-gray-400 line-through" : "text-gray-800"}`}>
                        {r.contact_name ? (
                          <ContactLink
                            identity={{ email: r.to_email, name: r.contact_name, phone: phoneOf(r) }}
                            className="text-left font-semibold text-[#3a99a3] hover:text-[#55C5D0] hover:underline"
                          >
                            {r.contact_name}
                          </ContactLink>
                        ) : "—"}
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {phone && !callable ? (
                          <span className="text-gray-600">{phone}</span>
                        ) : phone ? (
                          <a
                            href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                            onClick={() => setCalled((prev) => new Set(prev).add(r.id))}
                            title="Call this number"
                            className="inline-flex items-center gap-1.5 font-semibold text-[#55C5D0] hover:underline"
                          >
                            <Phone className="w-3.5 h-3.5" /> {phone}
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className={`py-1.5 pr-2 break-all ${done ? "text-gray-400" : "text-gray-600"}`}>{r.to_email || "—"}</td>
                      <td className={`py-1.5 whitespace-nowrap ${done ? "text-gray-400" : "text-gray-600"}`}>
                        {r[tsField] ? formatMelbourneDateTime(r[tsField]) : "—"}
                      </td>
                      {callable && (
                      <td className="py-1.5 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() =>
                            setActionsFor({
                              contactId: r.contact ?? null,
                              id: r.buyer ?? null,
                              name: r.contact_name || "",
                              email: r.to_email || "",
                              phone,
                            })
                          }
                          title="Log a call note, mark Purchased, set a reminder"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-[#55C5D0] hover:text-[#3a99a3]"
                        >
                          <ClipboardList className="w-3.5 h-3.5" /> Actions
                        </button>
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {actionsFor && (
        <BuyerActionsModal
          buyer={actionsFor}
          propertyId={null}
          onClose={() => setActionsFor(null)}
        />
      )}
    </div>
  );
}

/**
 * Read the message a send actually went out with.
 *
 * Christian Gravias (Monday 13057482228, 16 Sep 2026): "I want to also be able
 * to read the message that the agents sent at the campaign level." Until now the
 * only copy of a message was the personalised one on each recipient row, which
 * answers "what did James get" but never "what did the agent write".
 *
 * Works for either kind of row: a campaign reads its own body, a blast reads the
 * message stored on the send. Email is rendered in a sandboxed iframe - it is
 * third-party-ish HTML that must not reach the CRM's own DOM or run scripts.
 */
function SendMessageModal({ send, onClose }) {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const load =
      send.kind === "blast"
        ? fetchSendBatch(send.id).then((d) => d.message || {})
        : fetchCampaign(send.id).then((d) => ({
            subject: d.subject || "",
            body_html: d.channel === "sms" ? "" : d.html_body || "",
            body_text: d.channel === "sms" ? d.sms_body || "" : "",
          }));
    load
      .then((m) => active && setMessage(m))
      .catch(() => active && toast.error("Couldn't load the message"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [send.id, send.kind]);

  const html = (message?.body_html || "").trim();
  const text = (message?.body_text || "").trim();

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[820px] max-w-full max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 truncate">{send.name}</h3>
            {/* A draft has not been sent, so it gets neither a sender, a send
                time nor a recipient count - it would read as a send that went
                nowhere. */}
            <p className="text-xs text-gray-500 mt-0.5">
              {send.channel === "sms" ? "Text" : "Email"}
              {send.status === "draft"
                ? " · not sent yet"
                : <>
                    {send.created_by_name ? ` · sent by ${send.created_by_name}` : ""}
                    {send.sent_at ? ` · ${formatMelbourneDateTime(send.sent_at)}` : ""}
                    {` · ${(send.total_recipients || 0).toLocaleString()} recipient(s)`}
                  </>}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <>
              {message?.subject && (
                <p className="text-sm text-gray-800 mb-3">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-400 mr-2">
                    Subject
                  </span>
                  {message.subject}
                </p>
              )}
              {html ? (
                <iframe
                  title="Message"
                  sandbox=""
                  srcDoc={html}
                  className="w-full h-[52vh] border border-gray-200 rounded-lg bg-white"
                />
              ) : text ? (
                <pre className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-4 font-sans">
                  {text}
                </pre>
              ) : (
                <p className="text-sm text-gray-500">
                  No message was stored for this send.
                </p>
              )}
              {/* Merge tags are shown as written, not filled in: this is the
                  message the agent composed, and each recipient got their own
                  name substituted into it. */}
              {(html || text).includes("{{") && (
                <p className="text-[11px] text-gray-400 mt-3">
                  Shown as the agent wrote it. Tags like {"{{FirstName}}"} were
                  replaced with each person's own details when it went out.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CampaignsTab() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { campaign, mode } for the recipient drill-down
  // The send whose message is open for reading (Monday 13057482228).
  const [reading, setReading] = useState(null);

  // Whose campaigns. Same rule and the same controls as Buyer Enquiries and
  // Seller Leads: your own desk by default, the office only if you ask for it.
  // George Bottas, 4 Sep 2026: "Communcations showing the whole offices emails
  // being sent. Please only show my ones or the team I am assigned with."
  const {
    agencyWide,
    setAgencyWide,
    canSwitch: canScope,
    canIncludeAgents,
  } = useAgencyScope("communications");
  const { agentIds: extraAgentIds, setAgentIds: setExtraAgentIds, toggleAgentId } =
    useExtraAgents("communications", canIncludeAgents);

  const scopeParams = useMemo(() => {
    if (!canScope && !canIncludeAgents) return {};
    if (agencyWide) return { agency_wide: "true" };
    return extraAgentIds.length
      ? { agency_wide: "false", agents: extraAgentIds.join(",") }
      : { agency_wide: "false" };
  }, [canScope, canIncludeAgents, agencyWide, extraAgentIds]);

  const load = useCallback(() => {
    setLoading(true);
    fetchCampaigns(scopeParams)
      .then(setRows)
      .catch(() => toast.error("Couldn't load campaigns"))
      .finally(() => setLoading(false));
  }, [scopeParams]);

  useEffect(() => {
    load();
  }, [load]);

  // Only for the "include a colleague" picker, so only for people who have one.
  useEffect(() => {
    if (!canIncludeAgents) return;
    getAgents({ page_size: 200 })
      .then((res) => {
        const data = res?.data ?? {};
        setAgents(data.results ?? (Array.isArray(data) ? data : []));
      })
      .catch(() => setAgents([]));
  }, [canIncludeAgents]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await deleteCampaign(id);
      toast.success("Campaign deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onDuplicate = async (id) => {
    try {
      const c = await duplicateCampaign(id);
      toast.success("Duplicated");
      navigate(`/communications/campaigns/${c.id}`);
    } catch {
      toast.error("Duplicate failed");
    }
  };

  // How many rows are blasts rather than Campaign Manager campaigns. Worth
  // saying out loud: before this, those sends were simply missing from the tab
  // and an agent who had messaged 2,000 buyers that morning saw nothing here
  // (Monday 13057482228).
  //
  // Counted separately by where they were fired from, because "from Find
  // Buyers" was wrong for the ones sent off a listing's own Enquiries /
  // Attendees / Buyers tabs - and telling an agent their property send was a
  // Find Buyers send is the same confusion that started CRM-2026-00072.
  const blasts = rows.filter((r) => r.kind === "blast");
  const blastCount = blasts.length;
  const propertyBlastCount = blasts.filter((r) =>
    String(r.source || "").startsWith("property_"),
  ).length;
  const findBuyersCount = blastCount - propertyBlastCount;

  if (loading) return <p className="text-gray-400 text-sm p-6">Loading campaigns…</p>;

  return (
    <div>
      <div className="flex justify-between items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-gray-500">
            {rows.length} send{rows.length === 1 ? "" : "s"}
            {findBuyersCount > 0 && (
              <span className="text-gray-400">
                {" "}· {findBuyersCount} from Find Buyers
              </span>
            )}
            {propertyBlastCount > 0 && (
              <span className="text-gray-400">
                {" "}· {propertyBlastCount} from a listing
              </span>
            )}
          </p>
          {(canScope || canIncludeAgents) && (
            <>
              {canScope && (
                <AgencyScopeSwitch
                  agencyWide={agencyWide}
                  onChange={setAgencyWide}
                  mineLabel="My campaigns"
                  agencyLabel="All agents"
                />
              )}
              {!agencyWide && canIncludeAgents && (
                <IncludeAgentsPicker
                  agents={agents}
                  selectedIds={extraAgentIds}
                  onToggle={toggleAgentId}
                  onClear={() => setExtraAgentIds([])}
                  currentUserId={user?.id}
                />
              )}
            </>
          )}
        </div>
        <button
          onClick={() => navigate("/communications/campaigns/new")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
          <Megaphone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Nothing sent yet. Create your first email or SMS campaign.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Campaign</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Recipients</th>
                <th className="text-right px-4 py-3">Opened</th>
                <th className="text-right px-4 py-3">Clicked</th>
                {/* "Please also add an unsubscribed button rate so we can follow
                    those stats also" (Monday 13057482228). Counted against what
                    was delivered, the same base as opened and clicked. */}
                <th className="text-right px-4 py-3">Unsubscribed</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((c) => (
                <tr key={`${c.kind || "campaign"}-${c.id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {/* A blast has already gone out, so its name opens the
                        message it sent rather than an editor - there is no draft
                        behind it to open. */}
                    <button
                      onClick={() =>
                        c.kind === "blast"
                          ? setReading(c)
                          : navigate(`/communications/campaigns/${c.id}`)
                      }
                      className="flex items-center gap-2 text-left"
                    >
                      <ChannelIcon channel={c.channel} />
                      <div>
                        <p className="font-semibold text-gray-800">{c.name}</p>
                        <p className="text-xs text-gray-400">
                          {c.kind === "blast" && (
                            <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-bold text-[10px] uppercase tracking-wide align-middle">
                              {c.source_label || "Find Buyers"}
                            </span>
                          )}
                          {c.subject || c.channel_display}
                        </p>
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[c.status] || ""}`}>
                      {c.status_display}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setDrill({ campaign: c, mode: "sent" })}
                      className="text-gray-700 hover:text-[#25AFC0] hover:underline tabular-nums"
                      title="See who this was sent to"
                    >
                      {c.total_recipients}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setDrill({ campaign: c, mode: "opened" })}
                      className="text-gray-700 hover:text-[#25AFC0] hover:underline tabular-nums"
                      title="See who opened"
                    >
                      {c.opened_count} <span className="text-gray-400">({c.open_rate}%)</span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setDrill({ campaign: c, mode: "clicked" })}
                      className="text-gray-700 hover:text-[#25AFC0] hover:underline tabular-nums"
                      title="See who clicked"
                    >
                      {c.clicked_count} <span className="text-gray-400">({c.click_rate}%)</span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {/* Opens the names behind the number (Paul Jimoulis,
                        CRM-2026-00093: "I cant see who has unsubscribed"). They
                        are already blocked from every send, so the list is for
                        knowing who, never for messaging them again. */}
                    <button
                      onClick={() => setDrill({ campaign: c, mode: "unsubscribed" })}
                      className={`tabular-nums hover:text-[#25AFC0] hover:underline ${c.unsubscribed_count ? "text-amber-700" : "text-gray-700"}`}
                      title="See who unsubscribed from this send"
                    >
                      {c.unsubscribed_count || 0}{" "}
                      <span className="text-gray-400">({c.unsub_rate ?? 0}%)</span>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => setReading(c)}
                        title="Read the message that was sent"
                        className="text-gray-400 hover:text-gray-700"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {/* A blast is a receipt for a send that already happened:
                          nothing to duplicate into a draft, and deleting it would
                          delete the record of what went out. */}
                      {c.kind !== "blast" && (
                        <>
                          <button onClick={() => onDuplicate(c.id)} title="Duplicate" className="text-gray-400 hover:text-gray-700">
                            <Copy className="w-4 h-4" />
                          </button>
                          <button onClick={() => onDelete(c.id)} title="Delete" className="text-gray-400 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drill && (
        <CampaignRecipientsModal
          campaign={drill.campaign}
          mode={drill.mode}
          onClose={() => setDrill(null)}
        />
      )}

      {reading && (
        <SendMessageModal send={reading} onClose={() => setReading(null)} />
      )}
    </div>
  );
}

/* --------------------------------- Lists --------------------------------- */
const CONTACT_TYPES = ["buyer", "seller", "tenant", "landlord", "investor", "buyer_advocate", "general"];
const INTENT = ["cold", "warm", "active", "hot", "priority"];

// Criteria fields the "Include automatically" builder offers. Each maps to a
// key the backend segment_resolver understands. `op` is the (fixed) operator
// label shown in the middle column to match the mockup; the resolver ANDs
// across fields and ORs within a field's values.
const CRITERIA_FIELDS = [
  { key: "contact_type", label: "Contact type", op: "is any of", kind: "multi", options: CONTACT_TYPES },
  { key: "intent_category", label: "Buyer intent", op: "is any of", kind: "multi", options: INTENT },
  { key: "source", label: "Source", op: "is any of", kind: "multi", options: [] },
  { key: "state", label: "State", op: "is", kind: "text", placeholder: "VIC" },
  { key: "suburb", label: "Suburb", op: "in", kind: "text", placeholder: "Kew, Toorak" },
  { key: "tags", label: "Tag", op: "includes", kind: "text", placeholder: "VIP" },
];
const fieldDef = (key) => CRITERIA_FIELDS.find((f) => f.key === key) || CRITERIA_FIELDS[0];

// Turn the condition rows into a filter_json the backend resolver accepts.
// Conditions on the same field are merged into one OR-list.
function buildFilterJson(conditions) {
  const f = {};
  for (const c of conditions) {
    const def = fieldDef(c.field);
    const vals =
      def.kind === "multi"
        ? c.values || []
        : String(c.value || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    if (!vals.length) continue;
    f[c.field] = f[c.field] ? [...f[c.field], ...vals] : [...vals];
  }
  for (const k of Object.keys(f)) f[k] = [...new Set(f[k])];
  return f;
}

// Reverse of buildFilterJson: rehydrate a saved filter_json back into editable
// condition rows so the list editor opens pre-filled. One row per known field.
function conditionsFromFilterJson(filterJson) {
  const rows = [];
  for (const [field, raw] of Object.entries(filterJson || {})) {
    if (!CRITERIA_FIELDS.some((f) => f.key === field)) continue;
    const vals = Array.isArray(raw) ? raw : [raw];
    if (!vals.length) continue;
    const def = fieldDef(field);
    rows.push(
      def.kind === "multi"
        ? { id: ++_condId, field, values: vals.map(String), value: "" }
        : { id: ++_condId, field, values: [], value: vals.join(", ") }
    );
  }
  return rows.length ? rows : [newCondition()];
}

function ListsTab() {
  const navigate = useNavigate();
  // Needed to tell my own Database lists apart from the ones a colleague shared.
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null); // full segment detail being edited
  const [viewing, setViewing] = useState(null); // read-only Database list being viewed

  const load = useCallback(() => {
    setLoading(true);
    fetchSegments()
      .then(setRows)
      .catch(() => toast.error("Couldn't load lists"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this list?")) return;
    await deleteSegment(id);
    toast.success("List deleted");
    load();
  };

  const onEdit = async (id) => {
    try {
      // Detail view carries filter_json + pinned_members so the editor can
      // rehydrate; the list view omits pinned members for speed.
      const full = await fetchSegment(id);
      setEditing(full);
    } catch {
      toast.error("Couldn't open that list");
    }
  };

  if (loading) return <p className="text-gray-400 text-sm p-6">Loading lists…</p>;

  // Lists mirrored from Pipeline > Database are managed there, so they show as
  // their own read-only group here (Christian Gravias, 2026-08-03).
  const ownLists = rows.filter((s) => s.source !== "database");
  const dbLists = rows.filter((s) => s.source === "database");

  const Card = ({ s, readOnly }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex justify-between items-start">
        <button
          onClick={() => (readOnly ? setViewing(s) : onEdit(s.id))}
          className="text-left min-w-0"
        >
          <p className="font-semibold text-gray-800 truncate hover:text-[#2A8791]">{s.name}</p>
          <p className="text-xs text-gray-400">{s.description || s.segment_type_display}</p>
          {/* Whose Database it came from, when it isn't mine (George Bottas,
              Monday 12975288716). */}
          {readOnly && s.owner && currentUserId && String(s.owner) !== String(currentUserId) && (
            <p className="text-[11px] font-bold text-sky-600 mt-1">
              Shared with you by {s.owner_name || "another agent"}
            </p>
          )}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {readOnly ? (
            <button
              onClick={() => navigate("/database")}
              title="Managed in Pipeline > Database"
              className="text-[10px] font-bold uppercase tracking-wide text-[#2A8791] bg-[#eefcfd] rounded-full px-2 py-1"
            >
              Database
            </button>
          ) : (
            <>
              <button onClick={() => onEdit(s.id)} title="Edit list" className="text-gray-300 hover:text-[#2A8791]">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => onDelete(s.id)} title="Delete list" className="text-gray-300 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-3">
        {s.cached_count ?? s.live_count ?? "-"}
        <span
          className="text-xs font-normal text-gray-400 ml-1"
          title="Total contacts matching this list, before filtering by phone/email or consent - the number actually sent to may be lower"
        >
          total contacts
        </span>
      </p>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">{rows.length} list(s)</p>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> New List
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ownLists.map((s) => (
          <Card key={s.id} s={s} readOnly={false} />
        ))}
        {rows.length === 0 && (
          <p className="text-gray-400 text-sm col-span-full">No lists yet.</p>
        )}
      </div>

      {dbLists.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-1">
            <Database className="w-4 h-4 text-gray-400" />
            <h3 className="font-bold text-gray-800 text-sm">From the Database</h3>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Every list you build in Pipeline &gt; Database is here automatically — tick it as an
            audience in a campaign to email or text everyone on it. Rename or remove them in the
            Database. Lists a colleague has shared with you are here too, tagged with their name.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {dbLists.map((s) => (
              <Card key={s.id} s={s} readOnly />
            ))}
          </div>
        </div>
      )}

      {showNew && <ListModal onClose={() => setShowNew(false)} onSaved={load} />}
      {editing && (
        <ListModal
          segment={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
      {viewing && (
        <DatabaseListModal
          segment={viewing}
          onClose={() => setViewing(null)}
          onOpenDatabase={() => navigate("/database")}
        />
      )}
    </div>
  );
}

/**
 * Read-only view of a list mirrored from Pipeline > Database: who is on it and
 * how to reach them. Editing happens in the Database, so there is nothing to save
 * here (Christian Gravias, 2026-08-03).
 */
function DatabaseListModal({ segment, onClose, onOpenDatabase }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { total, count, rows }

  useEffect(() => {
    let active = true;
    fetchSegmentMembers(segment.id)
      .then((d) => active && setData(d))
      .catch(() => active && toast.error("Couldn't load who's on this list"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [segment.id]);

  const rows = data?.rows || [];

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[640px] max-w-full max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">{segment.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading ? "Loading…" : `${data?.total ?? rows.length} contact(s) · from your Database`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nobody on this list has an email or mobile yet, so there is no one to send to.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                  <th className="py-1.5">Name</th>
                  <th className="py-1.5">Email</th>
                  <th className="py-1.5">Mobile</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="py-1.5 pr-2 text-gray-800 font-semibold">{r.name || "—"}</td>
                    <td className="py-1.5 pr-2 text-gray-600 break-all">{r.email || "—"}</td>
                    <td className="py-1.5 text-gray-600">{r.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <p className="text-xs text-gray-400">
            Managed in Pipeline &gt; Database. Pick this list as an audience in a campaign to
            email or text everyone on it.
          </p>
          <button
            onClick={onOpenDatabase}
            className="px-3 py-1.5 rounded-lg bg-[#55C5D0] text-white text-xs font-bold shrink-0 ml-3"
          >
            Open Database
          </button>
        </div>
      </div>
    </div>
  );
}

let _condId = 0;
const newCondition = () => ({ id: ++_condId, field: "contact_type", values: [], value: "" });

function ConditionRow({ cond, onChange, onRemove, sourceOptions }) {
  const def = fieldDef(cond.field);
  const options = cond.field === "source" ? sourceOptions : def.options || [];

  const setField = (field) =>
    onChange({ ...cond, field, values: [], value: "" });
  const removeValue = (v) =>
    onChange({ ...cond, values: cond.values.filter((x) => x !== v) });

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2">
        <select
          value={cond.field}
          onChange={(e) => setField(e.target.value)}
          className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-2 text-sm"
        >
          {CRITERIA_FIELDS.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>

        <span className="shrink-0 text-xs text-gray-400 w-14 text-center">{def.op}</span>

        {def.kind === "multi" ? (
          <select
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (v && !cond.values.includes(v))
                onChange({ ...cond, values: [...cond.values, v] });
            }}
            className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-2 text-sm"
          >
            <option value="">Select…</option>
            {options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ) : (
          <input
            value={cond.value}
            onChange={(e) => onChange({ ...cond, value: e.target.value })}
            placeholder={def.placeholder}
            className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-2 text-sm"
          />
        )}

        <button
          onClick={onRemove}
          title="Remove condition"
          className="shrink-0 text-gray-300 hover:text-red-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {def.kind === "multi" && cond.values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5 pl-1">
          {cond.values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full"
            >
              {v}
              <button onClick={() => removeValue(v)} className="hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ListModal({ segment, onClose, onSaved }) {
  const isEdit = !!segment;
  const [name, setName] = useState(segment?.name || "");
  const [conditions, setConditions] = useState(() =>
    isEdit ? conditionsFromFilterJson(segment.filter_json) : [newCondition()]
  );
  const [pinned, setPinned] = useState(() =>
    isEdit && Array.isArray(segment.pinned_members) ? segment.pinned_members : []
  ); // [{id, name, email, phone}]
  const [sourceOptions, setSourceOptions] = useState([]);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  // "Paste emails" import: paste a blob -> parsed into individual addresses.
  const [pasteText, setPasteText] = useState("");
  const [pasteResult, setPasteResult] = useState(null); // {valid_count, matched, unknown_count, ...}
  const [pasteBusy, setPasteBusy] = useState(false);

  const [counts, setCounts] = useState({ count: 0, criteria_count: 0, pinned_count: 0 });
  const [counting, setCounting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFilterOptions()
      .then((o) => setSourceOptions(o.source || []))
      .catch(() => {});
  }, []);

  const filterJson = React.useMemo(() => buildFilterJson(conditions), [conditions]);
  // Filter keys the visual builder doesn't expose — e.g. the auto-segments
  // "Appraisals"/"Past Clients" resolve on appraised/past_client. Preserve them
  // so editing such a segment here never silently drops its criteria.
  const preservedFilterKeys = React.useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(segment?.filter_json || {})) {
      if (!CRITERIA_FIELDS.some((f) => f.key === k)) out[k] = v;
    }
    return out;
  }, [segment]);
  const effectiveFilterJson = React.useMemo(
    () => ({ ...preservedFilterKeys, ...filterJson }),
    [preservedFilterKeys, filterJson]
  );
  const pinnedIds = React.useMemo(() => pinned.map((p) => p.id), [pinned]);
  const hasCriteria = Object.keys(effectiveFilterJson).length > 0;

  // Pasted addresses that already match a contact (have an id) count toward the
  // live total right away; the unknown ones are created on save.
  const pasteMatchedIds = React.useMemo(
    () => (pasteResult?.matched || []).map((m) => m.id),
    [pasteResult]
  );
  const pasteNewCount = pasteResult?.unknown_count || 0;
  const pasteEmails = React.useMemo(() => pasteResult?.emails || [], [pasteResult]);
  // Total contacts that will be on the list (matched + new to be created).
  const countIds = React.useMemo(
    () => Array.from(new Set([...pinnedIds, ...pasteMatchedIds])),
    [pinnedIds, pasteMatchedIds]
  );

  // Live total (criteria ∪ pinned ∪ pasted-matches), debounced.
  useEffect(() => {
    if (!hasCriteria && countIds.length === 0) {
      setCounts({ count: 0, criteria_count: 0, pinned_count: 0 });
      return;
    }
    setCounting(true);
    const t = setTimeout(() => {
      previewSegment({ filter_json: effectiveFilterJson, pinned_contact_ids: countIds })
        .then((res) => setCounts(res))
        .catch(() => {})
        .finally(() => setCounting(false));
    }, 400);
    return () => clearTimeout(t);
  }, [effectiveFilterJson, countIds, hasCriteria]);

  // Parse the pasted email blob (debounced). Preview only - nothing is saved
  // until the list itself is saved.
  useEffect(() => {
    if (!pasteText.trim()) {
      setPasteResult(null);
      return;
    }
    setPasteBusy(true);
    const t = setTimeout(() => {
      parsePastedEmails(pasteText)
        .then((res) => setPasteResult(res))
        .catch(() => setPasteResult(null))
        .finally(() => setPasteBusy(false));
    }, 500);
    return () => clearTimeout(t);
  }, [pasteText]);

  // Contact search for "Manually add people", debounced.
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      searchContactsForList(query.trim())
        .then((rows) => setResults(rows))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const updateCond = (id, next) =>
    setConditions((cs) => cs.map((c) => (c.id === id ? next : c)));
  const removeCond = (id) => setConditions((cs) => cs.filter((c) => c.id !== id));
  const addCond = () => setConditions((cs) => [...cs, newCondition()]);

  const addPinned = (c) => {
    if (!pinned.some((p) => p.id === c.id)) setPinned((p) => [...p, c]);
    setQuery("");
    setResults([]);
  };
  const removePinned = (id) => setPinned((p) => p.filter((c) => c.id !== id));

  const save = async () => {
    if (!name.trim()) return toast.error("Give the list a name");
    if (!hasCriteria && pinnedIds.length === 0 && pasteEmails.length === 0)
      return toast.error("Add a condition, pin a contact, or paste some emails");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        segment_type: "dynamic",
        filter_json: effectiveFilterJson,
        pinned_contact_ids: pinnedIds,
      };
      if (pasteEmails.length) payload.paste_emails = pasteEmails;
      const res = isEdit
        ? await updateSegment(segment.id, payload)
        : await createSegment(payload);
      const created = res?.paste_result?.created || 0;
      const base = isEdit ? "List updated" : "List created";
      toast.success(created ? `${base} — ${created} new contact${created === 1 ? "" : "s"} added` : base);
      onSaved();
      onClose();
    } catch {
      toast.error(isEdit ? "Update failed" : "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-gray-800 mb-4">{isEdit ? "Edit list" : "New list"}</h3>

        {/* List name */}
        <label className="block text-xs font-medium text-gray-500 mb-1">List name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Priority buyers — Kew & Toorak"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
        />

        {/* Criteria */}
        <div className="flex items-center gap-2 mb-1.5">
          <Filter className="w-4 h-4 text-gray-400" />
          <p className="text-sm font-semibold text-gray-700">Include automatically, by criteria</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 mb-5">
          <p className="text-xs text-gray-400 mb-2">Match all of the following</p>
          {conditions.map((c) => (
            <ConditionRow
              key={c.id}
              cond={c}
              sourceOptions={sourceOptions}
              onChange={(next) => updateCond(c.id, next)}
              onRemove={() => removeCond(c.id)}
            />
          ))}
          <button
            onClick={addCond}
            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-gray-600 border border-gray-300 rounded-lg px-2.5 py-1.5 hover:bg-white"
          >
            <Plus className="w-3.5 h-3.5" /> Add condition
          </button>
          <p className="text-xs text-gray-400 mt-2.5">
            {hasCriteria
              ? `${counts.criteria_count} contacts match — updates automatically as records change.`
              : "No criteria yet — add a condition, or just pin people below."}
          </p>
        </div>

        {/* Manually add people */}
        <div className="flex items-center gap-2 mb-1.5">
          <UserPlus className="w-4 h-4 text-gray-400" />
          <p className="text-sm font-semibold text-gray-700">Manually add people</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="relative mb-2.5">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search contacts by name or phone"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
            />
            {results.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {results.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => addPinned(r)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex justify-between items-center"
                  >
                    <span className="text-sm text-gray-800">{r.name || "(no name)"}</span>
                    <span className="text-xs text-gray-400">{r.email || r.phone}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {pinned.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {pinned.map((p) => (
                <span
                  key={p.id}
                  className="inline-flex items-center gap-1.5 bg-[#E9F8F9] text-[#2A8791] text-xs pl-2.5 pr-1.5 py-1 rounded-full"
                >
                  {p.name || p.email || p.phone}
                  <button onClick={() => removePinned(p.id)} className="hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2.5">
            Pinned — stays on the list even if they don't match the criteria above.
          </p>
        </div>

        {/* Paste emails */}
        <div className="flex items-center gap-2 mb-1.5">
          <ClipboardList className="w-4 h-4 text-gray-400" />
          <p className="text-sm font-semibold text-gray-700">Paste emails</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"Paste a list of emails — commas, spaces or new lines all work.\ne.g. jane@example.com, sam@example.com, alex@work.com.au"}
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono resize-y"
          />
          {pasteBusy && (
            <p className="text-xs text-gray-400 mt-2">Reading emails…</p>
          )}
          {!pasteBusy && pasteResult && (
            <div className="mt-2.5 space-y-1.5">
              <div className="flex flex-wrap gap-1.5 text-xs">
                <span className="inline-flex items-center gap-1 bg-[#E9F8F9] text-[#2A8791] px-2 py-1 rounded-full font-semibold">
                  {pasteResult.valid_count} valid email{pasteResult.valid_count === 1 ? "" : "s"}
                </span>
                {pasteResult.matched_count > 0 && (
                  <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {pasteResult.matched_count} already a contact
                  </span>
                )}
                {pasteNewCount > 0 && (
                  <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-600 px-2 py-1 rounded-full">
                    {pasteNewCount} new — added to database on save
                  </span>
                )}
                {pasteResult.duplicates_removed > 0 && (
                  <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                    {pasteResult.duplicates_removed} duplicate{pasteResult.duplicates_removed === 1 ? "" : "s"} removed
                  </span>
                )}
              </div>
              {pasteResult.invalid?.length > 0 && (
                <p className="text-xs text-amber-600 flex items-start gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Skipped {pasteResult.invalid.length} that don't look like valid emails:{" "}
                    {pasteResult.invalid.slice(0, 5).join(", ")}
                    {pasteResult.invalid.length > 5 ? "…" : ""}
                  </span>
                </p>
              )}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2.5">
            Bulk-paste from a spreadsheet or email. Each address is separated and counted individually;
            duplicates and invalid entries are dropped. New addresses are added to your contacts.
          </p>
        </div>

        {/* Total */}
        <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 mb-4">
          <span className="text-sm text-gray-500">
            Total in list
            {pasteNewCount > 0 && (
              <span className="text-xs text-blue-600 ml-1">(incl. {pasteNewCount} new)</span>
            )}
          </span>
          <span className="text-xl font-semibold text-gray-900">
            {counting ? "…" : counts.count + pasteNewCount}
          </span>
        </div>

        {/* Opt-out note */}
        <div className="flex items-start gap-2 text-xs text-gray-500 mb-5">
          <ShieldCheck className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
          <span>Opted-out contacts are excluded automatically, even if manually added.</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-2 text-sm text-gray-500">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold disabled:opacity-50"
          >
            {saving ? "Saving…" : "Create list"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------- Auto groups: Appraisals / Past Clients ------------- */
// Dedicated read-only views of the two auto-updating segments, showing WHO is
// in each list (contact + linked property + date), not just a count.
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const AUTO_GROUP_PAGE_SIZE = 10; // members per page
const PROPS_COLLAPSE_AT = 3;      // properties shown per member before folding

function AutoGroupTab({ filterKey, title, blurb, Icon, showRoleToggle = false }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { total, count, rows }
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set()); // member ids showing all properties
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // name A-Z / Z-A
  const [propFilter, setPropFilter] = useState(""); // distinct property address
  const [monthFilter, setMonthFilter] = useState(""); // YYYY-MM
  const [roleView, setRoleView] = useState("all"); // all | buyer | seller (Past Clients)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const segs = await fetchSegments();
      const seg = (segs || []).find(
        (s) => s.filter_json && s.filter_json[filterKey] === true
      );
      if (!seg) {
        setData({ total: 0, count: 0, rows: [] });
        return;
      }
      setData(await fetchSegmentMembers(seg.id));
      setPage(1);
      setExpanded(new Set());
    } catch (err) {
      console.error("Auto-group load failed:", err);
      toast.error(`Failed to load ${title}`);
    } finally {
      setLoading(false);
    }
  }, [filterKey, title]);

  useEffect(() => {
    load();
  }, [load]);

  // Jump back to page 1 whenever a filter/sort changes.
  useEffect(() => {
    setPage(1);
  }, [search, propFilter, monthFilter, sortDir, roleView]);

  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allRows = useMemo(() => data?.rows || [], [data]);

  // Separate buyers (role 'purchased') from sellers (role 'listed'). When a side
  // is selected each member keeps only that side's properties, so the Property /
  // Date columns and the count reflect the chosen role (Monday 12567166271).
  const rows = useMemo(() => {
    if (!showRoleToggle || roleView === "all") return allRows;
    const want = roleView === "buyer" ? "purchased" : "listed";
    return allRows
      .map((r) => ({ ...r, properties: (r.properties || []).filter((p) => p.role === want) }))
      .filter((r) => (r.properties || []).length > 0);
  }, [allRows, roleView, showRoleToggle]);

  const total = (showRoleToggle && roleView !== "all")
    ? rows.length
    : (data?.total ?? rows.length);

  // Buyer / seller counts for the toggle labels.
  const roleCounts = useMemo(() => {
    let buyer = 0, seller = 0;
    allRows.forEach((r) => {
      const roles = new Set((r.properties || []).map((p) => p.role));
      if (roles.has("purchased")) buyer += 1;
      if (roles.has("listed")) seller += 1;
    });
    return { buyer, seller };
  }, [allRows]);

  // Distinct property + month options, derived from the loaded members.
  const propertyOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => (r.properties || []).forEach((p) => {
      const a = (p.address || p.suburb || "").trim();
      if (a) set.add(a);
    }));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const monthOptions = useMemo(() => {
    const m = new Map(); // 'YYYY-MM' -> label
    rows.forEach((r) => (r.properties || []).forEach((p) => {
      if (!p.date) return;
      const ym = String(p.date).slice(0, 7);
      if (ym && !m.has(ym)) {
        const d = new Date(p.date);
        m.set(ym, isNaN(d) ? ym : d.toLocaleDateString("en-AU", { month: "long", year: "numeric" }));
      }
    }));
    return Array.from(m.entries()).sort((a, b) => b[0].localeCompare(a[0])); // recent first
  }, [rows]);

  // Filter (search + property + month) then sort by name A-Z / Z-A.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = rows.filter((r) => {
      const props = r.properties || [];
      if (q) {
        const hay = [r.name, r.email, r.phone, ...props.map((p) => p.address), ...props.map((p) => p.suburb)]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (propFilter && !props.some((p) => (p.address || p.suburb || "").trim() === propFilter)) return false;
      if (monthFilter && !props.some((p) => String(p.date || "").slice(0, 7) === monthFilter)) return false;
      return true;
    });
    out.sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()));
    if (sortDir === "desc") out.reverse();
    return out;
  }, [rows, search, propFilter, monthFilter, sortDir]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading {title}…
      </div>
    );
  }

  const pageCount = Math.max(1, Math.ceil(filtered.length / AUTO_GROUP_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice(
    (safePage - 1) * AUTO_GROUP_PAGE_SIZE,
    safePage * AUTO_GROUP_PAGE_SIZE
  );

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Icon className="w-5 h-5 text-[#55C5D0]" /> {title}
            <span className="text-xs font-medium text-[#0e9f6e] bg-[#0e9f6e]/10 px-2 py-0.5 rounded-full">
              Auto-updating
            </span>
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">{blurb}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">
            <b className="text-gray-900">{total.toLocaleString()}</b> contact{total === 1 ? "" : "s"}
          </span>
          <button
            onClick={load}
            title="Refresh"
            className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {allRows.length === 0 ? (
        <div className="text-center text-gray-500 py-12 border border-dashed border-gray-200 rounded-xl">
          No contacts in this list yet.
        </div>
      ) : (
      <>
        {/* Buyers / Sellers separator (Past Clients only) */}
        {showRoleToggle && (
          <div className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5 mb-3">
            {[
              ["all", "All", allRows.length],
              ["buyer", "Buyers", roleCounts.buyer],
              ["seller", "Sellers", roleCounts.seller],
            ].map(([val, label, n]) => (
              <button
                key={val}
                onClick={() => setRoleView(val)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                  roleView === val ? "bg-white text-[#55C5D0] shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {label} <span className="text-xs text-gray-400">({n.toLocaleString()})</span>
              </button>
            ))}
          </div>
        )}

        {/* Filters: search · name A-Z/Z-A · property · month */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone or property…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
            />
          </div>
          <select
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value)}
            title="Sort by name"
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white"
          >
            <option value="asc">Sort: Ascending (A–Z)</option>
            <option value="desc">Sort: Descending (Z–A)</option>
          </select>
          <select
            value={propFilter}
            onChange={(e) => setPropFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white max-w-[220px]"
          >
            <option value="">All properties</option>
            {propertyOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white"
          >
            <option value="">All months</option>
            {monthOptions.map(([ym, label]) => (
              <option key={ym} value={ym}>{label}</option>
            ))}
          </select>
          {(search || propFilter || monthFilter || sortDir !== "asc") && (
            <button
              onClick={() => { setSearch(""); setPropFilter(""); setMonthFilter(""); setSortDir("asc"); }}
              className="px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
        <div className="text-center text-gray-500 py-12 border border-dashed border-gray-200 rounded-xl">
          No contacts match your filters.
        </div>
        ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="text-left font-semibold px-4 py-3">Name</th>
                  <th className="text-left font-semibold px-4 py-3">Contact</th>
                  <th className="text-left font-semibold px-4 py-3">Property</th>
                  <th className="text-left font-semibold px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => {
                  const props = r.properties || [];
                  const isOpen = expanded.has(r.id);
                  const shown = isOpen ? props : props.slice(0, PROPS_COLLAPSE_AT);
                  const hiddenCount = props.length - shown.length;
                  return (
                    <tr key={r.id} className="border-t border-gray-100 align-top">
                      <td className="px-4 py-3 font-semibold text-gray-900">{r.name || "—"}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="flex flex-col">
                          {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-gray-400" /> {r.email}</span>}
                          {r.phone && <span className="inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5 text-gray-400" /> {r.phone}</span>}
                          {!r.email && !r.phone && <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {props.length === 0 ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {shown.map((p, i) => (
                              <span key={i}>
                                {p.address || p.suburb || "—"}
                                {p.role_label && (
                                  <span className="ml-1.5 text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                    {p.role_label}
                                  </span>
                                )}
                              </span>
                            ))}
                            {props.length > PROPS_COLLAPSE_AT && (
                              <button
                                onClick={() => toggleExpanded(r.id)}
                                className="text-xs font-medium text-[#55C5D0] hover:underline text-left mt-0.5"
                              >
                                {isOpen ? "Show less" : `+${hiddenCount} more`}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {props.length === 0 ? "—" : (
                          <div className="flex flex-col gap-1">
                            {shown.map((p, i) => <span key={i}>{fmtDate(p.date)}</span>)}
                            {props.length > PROPS_COLLAPSE_AT && <span>&nbsp;</span>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 text-sm">
            <span className="text-gray-500">
              Showing {(safePage - 1) * AUTO_GROUP_PAGE_SIZE + 1}–
              {Math.min(safePage * AUTO_GROUP_PAGE_SIZE, filtered.length)} of {filtered.length.toLocaleString()}
              {filtered.length !== rows.length && ` (filtered from ${rows.length.toLocaleString()})`}
            </span>
            {pageCount > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-3 py-1 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-50"
                >
                  Prev
                </button>
                <span className="text-gray-500">Page {safePage} of {pageCount}</span>
                <button
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={safePage >= pageCount}
                  className="px-3 py-1 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
        )}
      </>
      )}
    </div>
  );
}

/* ------------------------------- Templates ------------------------------- */
// The audience tabs (Monday 12918907065). Mirrors the backend's
// MessageTemplate.AUDIENCE_CHOICES; the picker itself reads them from
// /communications/templates/audiences/, this copy only fills the editor dropdown.
const TEMPLATE_AUDIENCES = [
  ["buyers", "Buyers"],
  ["seller_leads", "Seller Leads"],
  ["vendors", "Vendors"],
  ["potential_landlords", "Potential Landlords"],
  ["current_landlords", "Current Landlords"],
  ["advocates", "Buyers Advocates"],
  ["portal", "Portal Sign-Up"],
  ["past_clients", "Past Clients"],
  ["reengagement", "Database Re-Engagement"],
  ["market", "Market Campaigns"],
  ["general", "General"],
];

const TEMPLATE_CATEGORIES = [
  ["general", "General"],
  ["market_update", "Market Update"],
  ["just_listed", "Just Listed"],
  ["just_sold", "Just Sold"],
  ["open_home", "Open Home"],
  ["auction_reminder", "Auction Reminder"],
  ["inspection_reminder", "Inspection Reminder"],
  ["finance_follow_up", "Finance Follow Up"],
  ["settlement", "Settlement Congratulations"],
  ["rental_update", "Rental Update"],
  ["portfolio_update", "Portfolio Update"],
  ["smsf_update", "SMSF Update"],
];

// Fallback merge-tag chips, used only if /communications/merge-tags/ can't be
// reached. The live list comes from the backend vocabulary
// (communications/services/merge_tags.py) - this modal used to hard-code these
// four while a template was free to reference any tag at all, and the send
// engine replaces an unknown tag with an empty string, so the mismatch shipped
// blank gaps in real messages (Monday 12918907065).
const SMS_MERGE_TAGS = ["FirstName", "Name", "Suburb", "State"];

// SMS credit count comes from the shared smsInfo() helper (GSM-7 vs Unicode
// aware, 160/153 or 70/67 char parts). Each part bills as 1 credit. It is always
// measured on asSent() - the typed body plus the two things the backend appends
// before delivery (UTM tracking on our own links, then the opt-out clause) -
// because pricing the typed body undercounts what actually goes out.

// Full-screen editor for a reusable email OR SMS template. Email is powered by
// the same drag-and-drop builder used in the campaign editor; SMS is a plain
// text composer with merge-tag chips and a live character / segment count.
function TemplateEditorModal({ template, onClose, onSaved }) {
  const isNew = !template?.id;
  const [channel, setChannel] = useState(template?.channel || "email");
  const [name, setName] = useState(template?.name || "");
  const [category, setCategory] = useState(template?.category || "general");
  // Audience + service/marketing class - the two axes Christian asked for
  // (Monday 12918907065). Audience is what the picker tabs by; `category` above
  // stays the topic (just_listed, auction_reminder), which the email starters
  // already use.
  const [audience, setAudience] = useState(template?.audience || "general");
  const [messageClass, setMessageClass] = useState(template?.message_class || "marketing");
  // The merge-tag chip list, fetched from the backend vocabulary rather than the
  // four hard-coded names this modal used to advertise - the mismatch between
  // that list and what templates actually referenced is the bug behind blank
  // gaps in sent messages. See communications/utils/mergeTags.js.
  const [vocab, setVocab] = useState(null);
  useEffect(() => {
    fetchMergeTags().then(setVocab).catch(() => {});
  }, []);
  const [subject, setSubject] = useState(template?.subject || "");
  const [previewText, setPreviewText] = useState(template?.preview_text || "");
  const [htmlBody, setHtmlBody] = useState(template?.html_body || "");
  const [designJson, setDesignJson] = useState(template?.design_json || {});
  const [smsBody, setSmsBody] = useState(template?.sms_body || "");
  const [saving, setSaving] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const smsRef = React.useRef(null);
  const isSms = channel === "sms";

  // Insert a raw token at the caret (or append) without clobbering the rest.
  const insertSmsToken = (token) => {
    const el = smsRef.current;
    if (!el) {
      setSmsBody((b) => b + token);
      return;
    }
    const start = el.selectionStart ?? smsBody.length;
    const end = el.selectionEnd ?? smsBody.length;
    const next = smsBody.slice(0, start) + token + smsBody.slice(end);
    setSmsBody(next);
    // Restore the caret just after the inserted token.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };
  const insertSmsTag = (tag) => insertSmsToken(`{{${tag}}}`);

  const save = async () => {
    if (!name.trim()) return toast.error("Give the template a name");
    if (isSms && !smsBody.trim()) return toast.error("Write the SMS message");
    setSaving(true);
    try {
      const payload = isSms
        ? { name, channel: "sms", category, audience, message_class: messageClass, sms_body: smsBody }
        : {
            name,
            channel: "email",
            category,
            audience,
            message_class: messageClass,
            subject,
            preview_text: previewText,
            html_body: htmlBody,
            design_json: designJson,
          };
      const saved = isNew ? await createTemplate(payload) : await updateTemplate(template.id, payload);
      toast.success(isNew ? "Template created" : "Template saved");
      onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed (migrate the DB first)");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-auto p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl my-4">
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <h3 className="font-bold text-gray-900">
            {isNew ? "New template" : "Edit template"}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {/* Channel picker - only for a brand-new template; switching after
              creation would strip the other channel's content. */}
          {isNew ? (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
                {[
                  { ch: "email", label: "Email", Icon: Mail },
                  { ch: "sms", label: "SMS", Icon: MessageSquare },
                ].map(({ ch, label, Icon }) => (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => setChannel(ch)}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-bold ${
                      channel === ch ? "bg-white text-[#55C5D0] shadow-sm" : "text-gray-500"
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">
              <ChannelIcon channel={channel} /> {isSms ? "SMS template" : "Email template"}
            </span>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Template name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {TEMPLATE_CATEGORIES.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Audience <span className="text-gray-400 font-normal">- the tab it appears under</span>
              </label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {TEMPLATE_AUDIENCES.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Classification</label>
              <select value={messageClass} onChange={(e) => setMessageClass(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="marketing">Marketing (bulk / prospecting)</option>
                <option value="service">Service (transactional, contact-specific)</option>
              </select>
              <p className="text-[11px] text-gray-400 mt-1">
                Marketing goes to a list and carries the unsubscribe clause. Service is
                transactional and specific to one contact.
              </p>
            </div>
            {!isSms && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Preview text</label>
                  <input value={previewText} onChange={(e) => setPreviewText(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </>
            )}
          </div>

          {isSms ? (
            <div>
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <label className="block text-xs font-medium text-gray-500">Message</label>
                <span className={`text-xs ${smsInfo(asSent(smsBody)).credits > 1 ? "text-amber-600" : "text-gray-400"}`}>
                  {smsBody.length} chars · {smsInfo(asSent(smsBody)).credits} credit{smsInfo(asSent(smsBody)).credits === 1 ? "" : "s"}
                  {smsInfo(asSent(smsBody)).encoding === "Unicode" ? " · Unicode" : ""}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(vocab?.tags?.map((t) => t.name) || SMS_MERGE_TAGS).map((tag) => {
                  const meta = vocab?.tags?.find((t) => t.name === tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => insertSmsTag(tag)}
                      title={
                        meta
                          ? `${meta.label} - ${
                              meta.scope === "compose"
                                ? "filled from the property/agent you pick in the composer"
                                : "filled in per person as the message sends"
                            }`
                          : tag
                      }
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        meta?.scope === "compose"
                          ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                          : "bg-[#E9F8F9] text-[#2A8791] hover:bg-[#d4f1f4]"
                      }`}
                    >
                      {`{{${tag}}}`}
                    </button>
                  );
                })}
                {/* Goes on the end of the text, and the send keeps it as the very last
                    line (Simon Abbott, Monday 13392775822). */}
                <button
                  type="button"
                  onClick={() => {
                    if (smsBody.includes(SMS_STOP_PLACEHOLDER)) {
                      toast.error("This message already has an unsubscribe link placeholder.");
                      return;
                    }
                    setSmsBody(appendStopPlaceholder(smsBody));
                  }}
                  className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200"
                >
                  Unsubscribe link
                </button>
              </div>
              <p className="text-[11px] text-gray-400 -mt-1 mb-2">
                An &quot;Unsubscribe&quot; link is always the last line when this template is sent.
              </p>
              <textarea
                ref={smsRef}
                value={smsBody}
                onChange={(e) => setSmsBody(e.target.value)}
                rows={6}
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
                placeholder="Hi {{FirstName}}, a new listing just hit the market in {{Suburb}}…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[11px] text-gray-400">
                Tap a merge tag to insert it. When a campaign starts from this template the agent's name and
                callback number are appended automatically.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Design - drag blocks; use {"{{FirstName}}"}, {"{{Suburb}}"} merge tags
              </label>
              <button
                type="button"
                onClick={() => setBuilderOpen(true)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#55C5D0] text-white text-sm font-bold hover:bg-[#3fb4bf] mb-3"
              >
                <FileText className="w-4 h-4" /> {htmlBody ? "Edit design (full screen)" : "Design template (full screen)"}
              </button>
              {htmlBody ? (
                <iframe
                  title="Template preview"
                  srcDoc={htmlBody}
                  className="w-full rounded-lg border border-gray-200 bg-white"
                  style={{ height: 420 }}
                />
              ) : (
                <div className="w-full rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-400">
                  Click <strong>Design template</strong> to build it with the drag-and-drop editor.
                </div>
              )}
              {builderOpen && (
                <EmailBuilder
                  title={name ? `Template - ${name}` : "Design template"}
                  design={designJson}
                  html={htmlBody}
                  preheader={previewText}
                  onChange={({ html_body, design_json }) => {
                    setHtmlBody(html_body);
                    setDesignJson(design_json);
                  }}
                  onClose={() => setBuilderOpen(false)}
                />
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-4">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-bold bg-[#55C5D0] text-white disabled:opacity-50">
            {saving ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TEMPLATE_FILTERS = [
  { id: "all", label: "All", icon: FileText },
  { id: "email", label: "Email", icon: Mail },
  { id: "sms", label: "SMS", icon: MessageSquare },
];

function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("all"); // all | email | sms
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [tplSearch, setTplSearch] = useState("");
  const [editing, setEditing] = useState(null); // template object, or {channel} for new

  const reload = useCallback(() => {
    setLoading(true);
    fetchTemplates()
      .then((r) => setRows(r?.results || r || []))
      .catch(() => toast.error("Couldn't load templates"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await deleteTemplate(t.id);
      setRows((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // With the seeded library the gallery is ~60 cards, so it needs the same
  // audience filter and search box as the picker (Monday 12918907065).
  const visible = rows.filter((t) => {
    if (channel !== "all" && t.channel !== channel) return false;
    if (audienceFilter !== "all" && (t.audience || "general") !== audienceFilter) return false;
    const q = tplSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (t.name || "").toLowerCase().includes(q) ||
      (t.sms_body || "").toLowerCase().includes(q) ||
      (t.subject || "").toLowerCase().includes(q)
    );
  });
  // "New template" defaults to the channel currently being viewed.
  const newChannel = channel === "sms" ? "sms" : "email";

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="text-sm text-gray-500">Reusable email &amp; SMS templates. Build once, start any campaign from them.</p>
        <button
          onClick={() => setEditing({ channel: newChannel })}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#55C5D0] text-white text-sm font-bold"
        >
          <Plus className="w-4 h-4" /> New {channel === "sms" ? "SMS " : channel === "email" ? "email " : ""}template
        </button>
      </div>

      {/* Channel filter */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4 w-fit">
        {TEMPLATE_FILTERS.map((f) => {
          const Icon = f.icon;
          const count = f.id === "all" ? rows.length : rows.filter((t) => t.channel === f.id).length;
          return (
            <button
              key={f.id}
              onClick={() => setChannel(f.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                channel === f.id ? "bg-white text-[#55C5D0] shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {f.label}
              <span className="text-[10px] text-gray-400">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Audience filter + search - the library is ~60 templates, a single
          scrolling gallery is not navigable (Christian Gravias). */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={tplSearch}
            onChange={(e) => setTplSearch(e.target.value)}
            placeholder="Search templates"
            aria-label="Search templates"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-1.5 text-sm"
          />
        </div>
        <select
          value={audienceFilter}
          onChange={(e) => setAudienceFilter(e.target.value)}
          aria-label="Filter by audience"
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="all">All audiences</option>
          {TEMPLATE_AUDIENCES.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm p-6">Loading templates…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4 group">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <ChannelIcon channel={t.channel} />
                  <p className="font-semibold text-gray-800 truncate">{t.name}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => setEditing(t)} title="Edit" className="p-1 text-gray-400 hover:text-[#55C5D0]">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(t)} title="Delete" className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {t.audience_display && (
                  <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-bold text-gray-500">
                    {t.audience_display}
                  </span>
                )}
                {t.message_class === "service" && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-50 text-[10px] font-bold text-blue-600">
                    SERVICE
                  </span>
                )}
                {t.is_library && (
                  <span
                    className="px-1.5 py-0.5 rounded bg-emerald-50 text-[10px] font-bold text-emerald-600"
                    title="Shared Collings template - admins only can edit it"
                  >
                    COLLINGS
                  </span>
                )}
                <span className="text-xs text-gray-400">{t.category_display}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2 line-clamp-2">{t.subject || t.sms_body}</p>
              <button
                onClick={() => setEditing(t)}
                className="mt-3 text-xs font-bold text-[#55C5D0] hover:underline"
              >
                {t.channel === "sms" ? "Edit message" : "Open in builder"}
              </button>
            </div>
          ))}
          {visible.length === 0 && (
            <p className="text-gray-400 text-sm col-span-full">
              No {channel === "all" ? "" : channel === "sms" ? "SMS " : "email "}templates yet. Click{" "}
              <strong>New {channel === "sms" ? "SMS " : channel === "email" ? "email " : ""}template</strong> to create one.
            </p>
          )}
        </div>
      )}

      {editing && (
        <TemplateEditorModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

/* --------------------------------- Shell --------------------------------- */
/* --------------------- Unsubscribe a contact (staff tool) --------------------- */
// For someone who asked to be removed but isn't a findable contact (they only
// exist as a raw lead / enquiry / attendee). Takes an email and/or a mobile and
// unsubscribes the WHOLE profile — both channels — plus the raw identifiers
// (Monday 13065245701).
function UnsubscribeEmailModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const emailValue = email.trim();
    const phoneValue = phone.trim();
    if (emailValue && !emailValue.includes("@")) {
      toast.error("That does not look like a valid email address.");
      return;
    }
    if (!emailValue && !phoneValue) {
      toast.error("Enter an email address or a mobile number.");
      return;
    }
    setBusy(true);
    try {
      await suppressEmail(emailValue, "", phoneValue);
      const who = [emailValue, phoneValue].filter(Boolean).join(" / ");
      toast.success(`${who} has been unsubscribed from emails and texts.`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't unsubscribe that person.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-500" /> Unsubscribe a contact
          </h3>
          <button onClick={onClose} className="text-gray-500"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <p className="text-sm text-gray-500">
            Use this for someone who asked to be removed but you can't find in Contacts.
            Enter their email, mobile, or both — we'll unsubscribe their whole profile
            from all future emails and texts.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            className="w-full p-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30"
            autoFocus
          />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0400 000 000"
            className="w-full p-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30"
          />
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg disabled:opacity-60"
            >
              {busy ? "Unsubscribing…" : "Unsubscribe"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CommunicationsPage() {
  const [tab, setTab] = useState("campaigns");
  const [unsubOpen, setUnsubOpen] = useState(false);
  // The sidebar badge on "Communications" is unread Messenger threads, but the
  // hub opens on Campaigns - so the number pointed at a page with nothing on it
  // (CG, 3 Sep 2026: "Whats this 2 in the red bubble for as there's nothing in
  // there... there needs to be the 2 here"). Same count, on the tab that holds
  // the messages.
  const messengerUnread = useSelector((s) => s.messenger?.unreadBadge || 0);
  const [sendEnabled, setSendEnabled] = useState(true);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    fetchCommsAnalytics()
      .then((d) => setSendEnabled(d.send_enabled))
      .catch(() => {});
    // Running out of SMS credits stops texts dead, so it is said at the top of
    // the whole hub rather than only inside a composer someone happens to open
    // (CG, Monday 12887883570: "like an alert if we run out of credits").
    fetchSmsCreditBalance().then(setCredits).catch(() => {});
  }, []);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Send className="w-6 h-6 text-[#55C5D0]" /> Communications Hub
          </h1>
          <p className="text-sm text-gray-500">
            Email & SMS campaigns, audiences and templates - powered by DOMA, sent through Brevo & SMS Broadcast.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setUnsubOpen(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
        >
          <Ban className="w-4 h-4" /> Unsubscribe a contact
        </button>
      </div>

      {unsubOpen && <UnsubscribeEmailModal onClose={() => setUnsubOpen(false)} />}

      {!sendEnabled && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Sending is OFF (safe mode).</strong> Campaigns are fully simulated - recipients are recorded but
            no email or SMS is delivered. An admin must set <code>COMMS_SEND_ENABLED</code> on production to send for real.
          </p>
        </div>
      )}

      {credits?.low && credits?.credits !== null && credits?.credits !== undefined && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>SMS credits are running low</strong> - {credits.credits.toLocaleString()} left.
            Texts stop sending at zero. Top up the Mobile Message account, and see{" "}
            <button type="button" onClick={() => setTab("sent")} className="underline font-semibold">
              Sent
            </button>{" "}
            for what has been going out.
          </p>
        </div>
      )}

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-5 w-fit max-w-full overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = t.id === "messenger" ? messengerUnread : 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              title={count > 0 ? `${count} unread message${count === 1 ? "" : "s"}` : undefined}
              className={`flex shrink-0 whitespace-nowrap items-center gap-2 px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition ${
                tab === t.id ? "bg-white text-[#55C5D0] shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              {count > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white shadow-sm">
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "campaigns" && <CampaignsTab />}
      {tab === "sent" && <SentActivityTab />}
      {tab === "unsubscribed" && <UnsubscribedTab />}
      {tab === "lists" && <ListsTab />}
      {tab === "appraisals" && (
        <AutoGroupTab
          filterKey="appraised"
          title="Appraisals"
          blurb="Contacts linked to a property that's been appraised. Updates automatically."
          Icon={Award}
        />
      )}
      {tab === "past_clients" && (
        <AutoGroupTab
          filterKey="past_client"
          title="Past Clients"
          blurb="Contacts who bought or sold with us on a completed sale. Updates automatically."
          Icon={Home}
          showRoleToggle
        />
      )}
      {tab === "templates" && <TemplatesTab />}
      {tab === "messenger" && <MessengerPage heightOffsetRem={16.5} />}
      {tab === "geevee_sms" && <GeeveeConversationsTab heightOffsetRem={20} />}
      {tab === "auto_responses" && <AutoEmailResponses />}
    </div>
  );
}
